import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useBack } from '../hooks';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { C, cs } from '../styles/common';
import PartForm, { PackagingLayersEditor, WrappingEditor, wrappingOf, COUNT_WORDS, PACK_PLACE_WORDS } from '../components/forms/PartForm';
import SharedPartDetail from '../components/PartDetail';
import InvoiceCard from '../components/InvoiceCard';
import ComplianceCard from '../components/ComplianceCard';
import { HistoryLink } from '../components/LastChanged';

export default function AdminParts() {
  const navigate = useNavigate();
  const goBack   = useBack('/admin');
  const [searchParams] = useSearchParams();
  const { confirm } = useToast();
  const [parts, setParts]           = useState([]);
  const [products, setProducts]     = useState([]);
  const [colours, setColours]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [recipeModalPart, setRecipeModalPart] = useState(null);
  const [showForm, setShowForm]     = useState(false);
  const [editPart, setEditPart]     = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [sortBy, setSortBy]         = useState('name');
  const [showProductParts, setShowProductParts] = useState(false);
  const [showDiscontinued, setShowDiscontinued] = useState(searchParams.get('discontinued') === 'show');
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [search, setSearch]                     = useState('');
  const [showSugg, setShowSugg]                 = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  function loadAll() {
    setLoading(true);
    Promise.all([
      api.get('/api/v1/parts?all=true'),
      api.get('/api/v1/products').catch(() => ({ data: [] })),
      api.get('/api/v1/colours').catch(() => ({ data: [] })),
    ]).then(([p, pr, co]) => {
      setParts(p.data);
      setProducts(pr.data);
      setColours(co.data);
      setLoading(false);
    });
  }

  function discontinue(id) {
    confirm({
      message: 'Discontinue this part? It will be hidden from active lists but can be restored at any time.',
      confirmLabel: 'Discontinue',
      danger: true,
      onConfirm: () => api.delete(`/api/v1/parts/${id}`).then(loadAll),
    });
  }

  function restore(id) {
    confirm({
      message: 'Restore this part to active status?',
      confirmLabel: 'Restore',
      onConfirm: () => api.post(`/api/v1/parts/${id}/restore`).then(loadAll),
    });
  }

  function toggleProduct(id) {
    setExpandedProducts(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function sortKey(type, data) {
    switch (sortBy) {
      case 'number':   return type === 'product' ? (data.code || '') : (data.part_number || data.name || '');
      case 'customer': return type === 'product' ? (data.product_parts?.[0]?.part?.customer || '') : (data.customer || '');
      case 'mould':    return type === 'product' ? (data.product_parts?.[0]?.part?.mould?.name || data.product_parts?.[0]?.part?.mould?.ref || '') : (data.mould?.name || data.mould?.ref || '');
      case 'machine':  return type === 'product' ? (data.product_parts?.[0]?.part?.primary_machine?.display_name || data.product_parts?.[0]?.part?.primary_machine?.name || '') : (data.primary_machine?.display_name || data.primary_machine?.name || '');
      default:         return type === 'product' ? (data.name || '') : (data.name || '');
    }
  }

  return (
    <div style={s.page}>
      {/* Breadcrumb */}
      <div style={s.breadcrumb}>
        <button onClick={goBack} style={s.backBtn}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
        </button>
        <span style={s.sep}>·</span>
        <span style={s.crumb}>Parts Management</span>
      </div>

      <div style={s.header}>
        <div>
          <h1 style={s.title}>Parts Management</h1>
          <p style={s.sub}>{parts.length} parts · {products.length} products</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <HistoryLink type="part" />
          <button style={{ ...s.addBtn, background: 'none', border: '1px solid #2a3040', color: '#f0f2f5' }}
            onClick={() => navigate('/admin/materials?tab=recipes')}>
            <i className="fa-solid fa-flask" style={{ marginRight: '6px', color: '#14b8a6' }} />Recipes
          </button>
<button style={{ ...s.addBtn, background: 'none', border: '1px solid #2a3040', color: '#f0f2f5' }}
            onClick={() => { setEditProduct(null); setShowProductForm(true); }}>
            <i className="fa-solid fa-layer-group" style={{ marginRight: '6px', color: '#14b8a6' }} />Create Product
          </button>
          <button style={s.addBtn} onClick={() => { setEditPart(null); setShowForm(true); }}>
            + Add Part
          </button>
        </div>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '16px' }}>
        <div style={s.searchWrap}>
          <i className="fa-solid fa-magnifying-glass" style={s.searchIcon} />
          <input
            style={s.searchInput}
            placeholder="Search by name, part number, customer…"
            value={search}
            onChange={e => { setSearch(e.target.value); setShowSugg(true); }}
            onFocus={() => setShowSugg(true)}
            onBlur={() => setTimeout(() => setShowSugg(false), 150)}
          />
          {search && <button style={s.clearBtn} onMouseDown={() => setSearch('')}>✕</button>}
        </div>
        {search && showSugg && (() => {
          const q = search.toLowerCase();
          const sugg = [];
          for (const p of products) {
            if ((p.name||'').toLowerCase().includes(q) || (p.code||'').toLowerCase().includes(q))
              sugg.push({ type: 'product', id: p.id, name: p.name, sub: p.code });
          }
          for (const p of parts) {
            if ((p.name||'').toLowerCase().includes(q) || (p.part_number||'').toLowerCase().includes(q) || (p.customer||'').toLowerCase().includes(q))
              sugg.push({ type: 'part', id: p.id, name: p.name, sub: p.part_number });
          }
          if (!sugg.length) return null;
          return (
            <div style={s.suggestions}>
              {sugg.slice(0, 8).map((item, i) => (
                <div key={i} style={s.suggItem}
                  onMouseDown={() => {
                    if (item.type === 'part') navigate(`/admin/parts/${item.id}`);
                    else { toggleProduct(item.id); setSearch(''); }
                    setShowSugg(false);
                  }}>
                  <i className={`fa-solid fa-${item.type === 'product' ? 'layer-group' : 'cube'}`}
                    style={{ fontSize: '11px', color: '#14b8a6', marginRight: '10px', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600' }}>{item.name}</div>
                    {item.sub && <div style={{ fontSize: '10px', color: '#14b8a6', fontFamily: 'monospace' }}>{item.sub}</div>}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {loading ? (
        <div style={s.center}>Loading...</div>
      ) : (
        <>
          {/* Sort bar */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: '#6b7280', marginRight: '4px' }}>Sort by</span>
            {[['name','Name'],['number','Number'],['customer','Customer'],['mould','Mould'],['machine','Machine']].map(([k,l]) => (
              <button key={k} onClick={() => setSortBy(k)}
                style={{ background: sortBy === k ? '#14b8a6' : 'none', color: sortBy === k ? '#fff' : '#9ca3af', border: `1px solid ${sortBy === k ? '#14b8a6' : '#2a3040'}`, borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', fontWeight: sortBy === k ? '700' : '400' }}>
                {l}
              </button>
            ))}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowDiscontinued(v => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: showDiscontinued ? '#78350f' : 'none',
                  color: showDiscontinued ? '#fcd34d' : '#6b7280',
                  border: `1px solid ${showDiscontinued ? '#b45309' : '#2a3040'}`,
                  borderRadius: '6px', padding: '4px 10px', fontSize: '11px',
                  cursor: 'pointer', fontWeight: '600', fontFamily: 'system-ui, sans-serif',
                }}>
                <i className={`fa-solid fa-eye${showDiscontinued ? '' : '-slash'}`} style={{ fontSize: '10px' }} />
                {showDiscontinued ? 'Showing discontinued' : 'Discontinued hidden'}
              </button>
              <button
                onClick={() => setShowProductParts(v => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: showProductParts ? '#0f766e' : 'none',
                  color: showProductParts ? '#5eead4' : '#6b7280',
                  border: `1px solid ${showProductParts ? '#0d9488' : '#2a3040'}`,
                  borderRadius: '6px', padding: '4px 10px', fontSize: '11px',
                  cursor: 'pointer', fontWeight: '600', fontFamily: 'system-ui, sans-serif',
                }}>
                <i className={`fa-solid fa-eye${showProductParts ? '' : '-slash'}`} style={{ fontSize: '10px' }} />
                {showProductParts ? 'Showing parts in products' : 'Parts in products hidden'}
              </button>
            </div>
          </div>

          <div style={s.table}>
            <div style={s.tableHeader}>
              <span></span>
              <span>Part / Product</span>
              <span>Customer</span>
              <span>Mould</span>
              <span>Machine</span>
              <span></span>
              <span>Material Recipe</span>
              <span>Actions</span>
            </div>
            {(() => {
              const partProductCount = {};
              products.forEach(prod => {
                (prod.product_parts || []).forEach(pp => {
                  const pid = pp.part?.id;
                  if (pid) partProductCount[pid] = (partProductCount[pid] || 0) + 1;
                });
              });
              const activeParts = showDiscontinued ? parts : parts.filter(p => p.status !== 'discontinued');
              const activeProducts = showDiscontinued ? products : products.filter(p => !p.discontinued);
              const visibleParts = showProductParts
                ? activeParts
                : activeParts.filter(p => !partProductCount[p.id]);
              const q = search.toLowerCase();
              let items = [
                ...activeProducts.map(p => ({ type: 'product', data: p })),
                ...visibleParts.map(p => ({ type: 'part', data: p })),
              ].sort((a, b) => sortKey(a.type, a.data).toLowerCase().localeCompare(sortKey(b.type, b.data).toLowerCase()));

              if (search) {
                items = items.filter(item => {
                  if (item.type === 'product') {
                    const p = item.data;
                    return (p.name||'').toLowerCase().includes(q) || (p.code||'').toLowerCase().includes(q) ||
                      (p.product_parts||[]).some(pp =>
                        (pp.part?.name||'').toLowerCase().includes(q) || (pp.part?.part_number||'').toLowerCase().includes(q)
                      );
                  } else {
                    const p = item.data;
                    return (p.name||'').toLowerCase().includes(q) || (p.part_number||'').toLowerCase().includes(q) || (p.customer||'').toLowerCase().includes(q);
                  }
                });
              }

              if (items.length === 0) return (
                <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                  No results for "{search}"
                </div>
              );

              return items.map(item => {
                if (item.type === 'product') {
                  const product = item.data;
                  const isExpanded = expandedProducts.has(product.id);
                  return (
                    <React.Fragment key={`product-${product.id}`}>
                      <div style={{ ...s.tableRow, background: '#161b22', cursor: 'pointer' }}
                        onClick={() => toggleProduct(product.id)}
                        onMouseEnter={e => e.currentTarget.style.background = '#1a2030'}
                        onMouseLeave={e => e.currentTarget.style.background = '#161b22'}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className={`fa-solid fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{ fontSize: '10px', color: '#14b8a6' }} />
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="fa-solid fa-layer-group" style={{ fontSize: '11px', color: product.discontinued ? '#b45309' : '#14b8a6' }} />
                            <span style={{ ...s.partName, color: product.discontinued ? '#fcd34d' : '#14b8a6' }}>{product.name}</span>
                            {product.discontinued && (
                              <span style={{ fontSize: '9px', background: '#78350f', color: '#fcd34d', border: '1px solid #b45309', borderRadius: '4px', padding: '1px 5px', flexShrink: 0 }}>
                                Discontinued
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '2px' }}>
                            <span style={s.partNum}>{product.code}</span>
                            <span style={{ fontSize: '10px', color: '#4b5563' }}>{product.product_parts?.length || 0} parts</span>
                          </div>
                        </div>
                        <div style={s.cell}>{product.product_parts?.[0]?.part?.customer || '—'}</div>
                        <div style={s.cell}>—</div>
                        <div style={s.cell}>—</div>
                        <div />
                        <div style={s.cell}>—</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} onClick={e => e.stopPropagation()}>
                          <button style={s.iconEdit} title="Edit product" onClick={() => { setEditProduct(product); setShowProductForm(true); }}>
                            <i className="fa-solid fa-pen" />
                          </button>
                          <button style={s.iconDel} title="Delete product" onClick={() => {
                            confirm({
                              message: `Delete product "${product.name}"? Parts will not be deleted.`,
                              confirmLabel: 'Delete',
                              danger: true,
                              onConfirm: () => api.delete(`/api/v1/products/${product.id}`).then(loadAll),
                            });
                          }}>
                            <i className="fa-solid fa-trash" />
                          </button>
                        </div>
                      </div>
                      {isExpanded && (product.product_parts || []).sort((a, b) => (a.part?.name || '').localeCompare(b.part?.name || '')).map(pp => {
                        const partFull = parts.find(p => p.id === pp.part?.id) || {};
                        const hex = colours.find(c => c.name === partFull.colour)?.hex;
                        return (
                          <div key={pp.id} style={{ ...s.tableRow, background: '#0f1117', paddingLeft: '32px', cursor: 'pointer', borderLeft: '3px solid #14b8a620' }}
                            onClick={() => navigate(`/admin/parts/${pp.part?.id}`)}
                            onMouseEnter={e => e.currentTarget.style.background = '#141a24'}
                            onMouseLeave={e => e.currentTarget.style.background = '#0f1117'}>
                            <div />
                            <div>
                              <div style={s.partName}>{pp.part?.name}</div>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '2px' }}>
                                <span style={s.partNum}>{pp.part?.part_number || '—'}</span>
                                <span style={{ fontSize: '10px', background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '4px', padding: '1px 6px' }}>×{pp.qty_per_assembly}</span>
                                {pp.build_step && <span title="Build step: when it is needed in assembly" style={{ fontSize: '10px', background: 'rgba(20,184,166,0.10)', border: '1px solid rgba(20,184,166,0.35)', color: '#14b8a6', borderRadius: '4px', padding: '1px 6px' }}>step {pp.build_step}</span>}
                              </div>
                            </div>
                            <div style={s.cell}>{pp.part?.customer || '—'}</div>
                            <div style={s.cell}>{pp.part?.mould?.name || pp.part?.mould?.ref || '—'}</div>
                            <div style={s.cell}>{pp.part?.primary_machine?.display_name || pp.part?.primary_machine?.name || '—'}</div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {partFull.colour && <div title={partFull.colour} style={{ width: '12px', height: '12px', borderRadius: '50%', background: hex || '#6b7280', border: '1px solid rgba(255,255,255,0.15)', flexShrink: 0 }} />}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                              <RecipeSummaryCell part={partFull} onOpen={() => setRecipeModalPart(partFull)} />
                            </div>
                            <div />
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                } else {
                  const part = item.data;
                  return (
                    <div key={`part-${part.id}`} style={{ ...s.tableRow, cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/parts/${part.id}`)}
                      onMouseEnter={e => e.currentTarget.style.background = '#242b35'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <div />
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ ...s.partName, ...(part.status === 'discontinued' ? { color: '#fcd34d' } : {}) }}>{part.name}</span>
                          {part.status === 'discontinued' && (
                            <span style={{ fontSize: '9px', background: '#78350f', color: '#fcd34d', border: '1px solid #b45309', borderRadius: '4px', padding: '1px 5px', flexShrink: 0 }}>
                              Discontinued
                            </span>
                          )}
                          {partProductCount[part.id] > 0 && (
                            <span style={{ fontSize: '9px', background: '#0f766e', color: '#5eead4', border: '1px solid #0d9488', borderRadius: '4px', padding: '1px 5px', flexShrink: 0 }}>
                              {partProductCount[part.id]} product{partProductCount[part.id] > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <div style={s.partNum}>{part.part_number || '—'}</div>
                      </div>
                      <div style={s.cell}>{part.customer || '—'}</div>
                      <div style={s.cell}>{part.mould?.name || part.mould?.ref || '—'}</div>
                      <div style={s.cell}>{part.primary_machine?.display_name || part.primary_machine?.name || '—'}</div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {part.colour ? (() => {
                          const hex = colours.find(c => c.name === part.colour)?.hex;
                          return <div title={part.colour} style={{ width: '12px', height: '12px', borderRadius: '50%', background: hex || '#6b7280', border: '1px solid rgba(255,255,255,0.15)', flexShrink: 0 }} />;
                        })() : null}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                        <RecipeSummaryCell part={part} onOpen={() => setRecipeModalPart(part)} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} onClick={e => e.stopPropagation()}>
                        <button style={s.iconEdit} title="Edit part" onClick={() => { setEditPart(part); setShowForm(true); }}>
                          <i className="fa-solid fa-pen" />
                        </button>
                        {part.status === 'discontinued' ? (
                          <button style={s.iconRestore} title="Restore part" onClick={() => restore(part.id)}>
                            <i className="fa-solid fa-rotate-left" />
                          </button>
                        ) : (
                          <button style={s.iconDel} title="Discontinue part" onClick={() => discontinue(part.id)}>
                            <i className="fa-solid fa-ban" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                }
              });
            })()}
          </div>
        </>
      )}

      {showForm && (
        <PartForm
          part={editPart}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); loadAll(); }}
        />
      )}
      {showProductForm && (
        <ProductFormModal
          product={editProduct}
          parts={parts}
          onClose={() => setShowProductForm(false)}
          onSaved={() => { setShowProductForm(false); loadAll(); }}
        />
      )}
      {recipeModalPart && (
        <RecipeModal part={recipeModalPart} onClose={() => setRecipeModalPart(null)} />
      )}
    </div>
  );
}

// ── RECIPE SUMMARY HELPERS ────────────────────────────────────
function RecipeSummaryCell({ part, onOpen }) {
  const prs = part.part_recipes || [];
  if (prs.length === 0) return <span style={{ fontSize: '12px', color: '#4b5563' }}>—</span>;
  if (prs.length > 1) return (
    <button onClick={onOpen} style={{ background: 'none', border: 'none', color: '#14b8a6', fontSize: '12px', cursor: 'pointer', padding: 0, fontFamily: 'system-ui, sans-serif', fontWeight: '600' }}>
      Multiple recipes
    </button>
  );
  const recipe = prs[0].recipe;
  const comps = recipe?.recipe_components || [];
  if (comps.length === 0) return <span style={{ fontSize: '12px', color: '#4b5563' }}>—</span>;
  if (comps.length <= 3) return (
    <span style={{ fontSize: '11px', color: '#9ca3af', lineHeight: '1.4' }}>
      {comps.map((c, i) => (
        <span key={i}>{i > 0 ? ' · ' : ''}<strong style={{ color: '#f0f2f5' }}>{c.percentage}%</strong> {c.material?.name}</span>
      ))}
    </span>
  );
  return (
    <button onClick={onOpen} style={{ background: 'none', border: 'none', color: '#14b8a6', fontSize: '12px', cursor: 'pointer', padding: 0, fontFamily: 'system-ui, sans-serif', fontWeight: '600', textAlign: 'left', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {recipe?.name || 'View recipe'}
    </button>
  );
}

function RecipeModal({ part, onClose }) {
  const prs = part.part_recipes || [];
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>Recipes — {part.name}</div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '16px 20px', maxHeight: '70vh', overflowY: 'auto' }}>
          {prs.map(pr => {
            const recipe = pr.recipe;
            const comps = recipe?.recipe_components || [];
            return (
              <div key={pr.id} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #2a3040' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700' }}>{recipe?.name}</span>
                  {pr.is_primary && <span style={{ fontSize: '9px', background: '#14b8a6', color: '#fff', padding: '1px 5px', borderRadius: '3px' }}>Primary</span>}
                </div>
                {recipe?.material && <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>{recipe.material.name}</div>}
                {comps.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: i < comps.length - 1 ? '1px solid #1a1f2a' : 'none' }}>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>{c.material?.name}</span>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#f0f2f5' }}>{c.percentage}%</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── PRODUCT FORM MODAL ────────────────────────────────────────
function ProductFormModal({ product, parts, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: product?.code || '',
    name: product?.name || '',
    discontinued: product?.discontinued || false,
    discontinued_date: product?.discontinued_date || '',
    // How the finished product is packed (051)
    packaging_layers: product?.packaging_layers || [],
    packing_count_method: product?.packing_count_method || '',
    packing_place: product?.packing_place || '',
    // One product as shipped, without packaging (071, 15 Oct 2026).
    weight_g: product?.weight_g ?? '',
    // How its pallets stack on the lorry (072) and are wrapped (074): in the
    // Packaging section, as on the part form and the Packaging page.
    ...wrappingOf(product),
  });
  const [containers, setContainers] = useState([]);
  useEffect(() => {
    api.get('/api/v1/packaging-containers').then(r => setContainers(r.data || [])).catch(() => {});
  }, []);
  // "Other…": the typed container joins the list for everyone.
  function addContainer(name) {
    return api.post('/api/v1/packaging-containers', { name }).then(r => {
      setContainers(cs => (cs.some(c => c.id === r.data.id) ? cs
        : [...cs, r.data].sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name))));
      return r.data;
    });
  }
  const machineOf = (m) => (m ? { id: m.id, name: m.display_name || m.name } : null);
  // The build order is the list's own order (the director, 8 Oct 2026: "let
  // the app number them in order they are set to be produced"): parts that
  // have a step come first, in step order.
  const [productParts, setProductParts] = useState(() =>
    (product?.product_parts || [])
      .map((pp, i) => ({ part_id: pp.part?.id, name: pp.part?.name, part_number: pp.part?.part_number, qty: pp.qty_per_assembly,
        step: pp.build_step || null, machine: machineOf(pp.part?.primary_machine), was: i }))
      .sort((a, b) => ((a.step || 999) - (b.step || 999)) || (a.was - b.was))
  );
  const [buildOn, setBuildOn] = useState(() => (product?.product_parts || []).some(pp => pp.build_step));
  // How long before an order's cut-off the build order takes over (Settings).
  const [buildWindow, setBuildWindow] = useState(null);
  useEffect(() => {
    api.get('/api/v1/products/build-order/window').then(r => setBuildWindow(r.data.working_days)).catch(() => {});
  }, []);
  const [partSearch, setPartSearch] = useState('');
  const [error, setError] = useState('');
  // Which parts and how many come from its Build steps in Stock, once it has
  // them (29 Oct 2026): kept in one place. The order (▲▼) stays here.
  const navigate = useNavigate();
  const [fromBuild, setFromBuild] = useState(false);
  useEffect(() => {
    if (!product?.id) return;
    api.get(`/api/v1/stock/products/${product.id}/build`)
      .then(r => setFromBuild(((r.data.items || {})[r.data.final_id]?.inputs || []).length > 0))
      .catch(() => setFromBuild(false));
  }, [product?.id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const existingPartIds = new Set(productParts.map(p => p.part_id));
  const matchingParts = parts.filter(p =>
    !existingPartIds.has(p.id) &&
    (p.name.toLowerCase().includes(partSearch.toLowerCase()) ||
     (p.part_number || '').toLowerCase().includes(partSearch.toLowerCase()))
  ).slice(0, 8);

  function addPart(part) {
    setProductParts(pp => [...pp, { part_id: part.id, name: part.name, part_number: part.part_number, qty: 1, step: null, machine: machineOf(part.primary_machine) }]);
    setPartSearch('');
  }
  function removePart(partId) {
    setProductParts(pp => pp.filter(p => p.part_id !== partId));
  }
  // Move a part one place up or down: its place is its build step.
  function movePart(index, dir) {
    setProductParts(pp => {
      const j = index + dir;
      if (j < 0 || j >= pp.length) return pp;
      const next = pp.slice();
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  }
  // Parts that share a machine: there, the build order decides which is
  // moulded first when an order runs tight.
  const sharing = {};
  for (const p of productParts) if (p.machine) (sharing[p.machine.id] = sharing[p.machine.id] || []).push(p);
  function updateQty(partId, qty) {
    setProductParts(pp => pp.map(p => p.part_id === partId ? { ...p, qty: parseInt(qty) || 1 } : p));
  }

  function save() {
    if (!form.code || !form.name) { setError('Code and name are required'); return; }
    setSaving(true); setError('');
    const w = String(form.weight_g ?? '').trim().replace(',', '.');
    if (w !== '' && !(Number(w) > 0)) { setError('The product weight is a number of grams, more than 0 — for example 41.5.'); return; }
    const payload = { ...form, weight_g: w === '' ? null : Number(w), discontinued_date: form.discontinued_date || null, parts: productParts.map((p, i) => ({ part_id: p.part_id, qty: p.qty, step: buildOn ? i + 1 : null })) };
    const req = product
      ? api.put(`/api/v1/products/${product.id}`, payload)
      : api.post('/api/v1/products', payload);
    req.then(() => onSaved()).catch(err => { setError(err.response?.data?.error || 'Save failed'); setSaving(false); });
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>{product ? `Edit — ${product.name}` : 'Create Product'}</div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          <Section title="Identity">
            <Field label="Product code *" value={form.code} onChange={v => set('code', v)} />
            <Field label="Product name *" value={form.name} onChange={v => set('name', v)} />
            <Field label="Weight of one product (g)" value={form.weight_g} onChange={v => set('weight_g', v)}
              hint="As shipped — its parts and any purchased elements — without packaging. Used for the weights on invoices." />
            <div style={s.field}>
              <label style={s.label}>Discontinued</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', width: 'fit-content' }}>
                <input type="checkbox" checked={form.discontinued} onChange={e => { set('discontinued', e.target.checked); if (!e.target.checked) set('discontinued_date', ''); }} />
                <span style={{ fontSize: '13px', color: form.discontinued ? '#fca5a5' : '#9ca3af' }}>Mark as discontinued</span>
              </label>
              {form.discontinued && (
                <div style={{ marginTop: '8px' }}>
                  <label style={s.label}>Discontinued date (optional)</label>
                  <input type="date" style={s.input} value={form.discontinued_date} onChange={e => set('discontinued_date', e.target.value)} />
                </div>
              )}
            </div>
          </Section>

          <Section title="Components (BOM)">
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', margin: '0 0 10px' }}>
              <input type="checkbox" checked={buildOn} onChange={e => setBuildOn(e.target.checked)} style={{ marginTop: '3px', accentColor: '#14b8a6' }} />
              <span style={{ fontSize: '12px', color: '#9ca3af', lineHeight: 1.5 }}>
                <b style={{ color: '#f0f2f5' }}>Build order: the parts below in the order they are produced</b> (▲ ▼ to arrange; the app numbers them).
                While there is time, base material and the colour order (Settings) decide the order on a machine. When an order would finish in its last {buildWindow ?? 10} working days before the cut-off, parts that share a machine are moulded in this order, so assembly can start sooner.
              </span>
            </label>
            {productParts.map((pp, i) => (
              <div key={pp.part_id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', marginBottom: '6px' }}>
                {buildOn && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                    <span title="Build step: produced in this order" style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(20,184,166,0.14)', color: '#14b8a6', fontSize: '11px', fontWeight: 800, display: 'grid', placeItems: 'center' }}>{i + 1}</span>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <button onClick={() => movePart(i, -1)} disabled={i === 0} aria-label={`Move ${pp.name} up`}
                        style={{ background: 'none', border: 'none', color: i === 0 ? '#2a3040' : '#9ca3af', cursor: i === 0 ? 'default' : 'pointer', fontSize: '10px', lineHeight: 1, padding: '1px 3px' }}>▲</button>
                      <button onClick={() => movePart(i, 1)} disabled={i === productParts.length - 1} aria-label={`Move ${pp.name} down`}
                        style={{ background: 'none', border: 'none', color: i === productParts.length - 1 ? '#2a3040' : '#9ca3af', cursor: i === productParts.length - 1 ? 'default' : 'pointer', fontSize: '10px', lineHeight: 1, padding: '1px 3px' }}>▼</button>
                    </div>
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600' }}>{pp.name}</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                    {pp.machine ? pp.machine.name : 'no machine set'}
                    {pp.part_number && <span style={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace', marginLeft: 6 }}>{pp.part_number}</span>}
                  </div>
                  {buildOn && pp.machine && (sharing[pp.machine.id] || []).length > 1 && (
                    <div style={{ fontSize: '11px', color: '#f59e0b' }}>
                      Shares {pp.machine.name} with {sharing[pp.machine.id].filter(o => o.part_id !== pp.part_id).map(o => o.name).join(', ')}: this order decides which goes first.
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>×</span>
                  {fromBuild
                    ? <span style={{ fontSize: '13px', fontWeight: 700, minWidth: '28px', textAlign: 'center' }} title="From its Build steps in Stock">{pp.qty}</span>
                    : <input type="number" min="1" value={pp.qty}
                        onChange={e => updateQty(pp.part_id, e.target.value)}
                        style={{ ...s.input, width: '60px', padding: '4px 8px', textAlign: 'center' }} />}
                </div>
                {!fromBuild && (
                  <button onClick={() => removePart(pp.part_id)}
                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', flexShrink: 0 }}>
                    <i className="fa-regular fa-trash-can" />
                  </button>
                )}
              </div>
            ))}

            {fromBuild ? (
              <div style={{ fontSize: '12px', color: '#9ca3af', lineHeight: 1.5, marginTop: '4px' }}>
                Which parts and how many come from its Build steps in Stock, so they are kept in one place; the order above is set here.{' '}
                <button type="button" onClick={() => navigate(`/stock/build/${product.id}`)} style={{ background: 'none', border: 'none', padding: 0, color: '#14b8a6', fontWeight: 700, cursor: 'pointer', fontSize: '12px' }}>Change them in Build steps</button>
              </div>
            ) : (
              <>
            <div style={{ position: 'relative' }}>
              <input
                value={partSearch}
                onChange={e => setPartSearch(e.target.value)}
                placeholder="Search parts to add…"
                style={{ ...s.input, width: '100%', boxSizing: 'border-box', marginTop: '4px' }}
              />
              {partSearch && matchingParts.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#1e2329', border: '1px solid #2a3040', borderRadius: '6px', zIndex: 10, marginTop: '2px', maxHeight: '200px', overflowY: 'auto' }}>
                  {matchingParts.map(p => (
                    <div key={p.id}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #2a3040', fontSize: '13px' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#242b35'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                      onClick={() => addPart(p)}>
                      <span style={{ fontWeight: '600' }}>{p.name}</span>
                      {p.part_number && <span style={{ fontSize: '11px', color: '#14b8a6', fontFamily: 'monospace', marginLeft: '8px' }}>{p.part_number}</span>}
                    </div>
                  ))}
                </div>
              )}
              {partSearch && matchingParts.length === 0 && (
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>No matching parts</div>
              )}
            </div>
              </>
            )}
          </Section>

          <Section title="Packaging">
            <div style={{ fontSize: '12px', color: '#9ca3af', lineHeight: 1.5, marginBottom: '10px' }}>
              How the finished product is packed, from the product outwards: for example a plastic bag, then a product box, then 15 product boxes to a transport box.
            </div>
            <PackagingLayersEditor layers={form.packaging_layers} containers={containers} label="Packed as — from the product outwards" unit="products"
              onChange={layers => set('packaging_layers', layers)} onAddContainer={addContainer} />
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '0 12px' }}>
              <div style={s.field}>
                <label style={s.label}>How the count is checked</label>
                <select style={s.input} value={form.packing_count_method} onChange={e => set('packing_count_method', e.target.value)}>
                  <option value="">Not said</option>
                  {Object.entries(COUNT_WORDS).map(([k, w]) => <option key={k} value={k}>{w}</option>)}
                </select>
              </div>
              <div style={s.field}>
                <label style={s.label}>Where it is packed</label>
                <select style={s.input} value={form.packing_place} onChange={e => set('packing_place', e.target.value)}>
                  <option value="">Not said</option>
                  {Object.entries(PACK_PLACE_WORDS).map(([k, w]) => <option key={k} value={k}>{w}</option>)}
                </select>
              </div>
            </div>
            <WrappingEditor form={form} set={set} containers={containers} />
          </Section>

          {/* Directors only, saved on its own (15 Oct 2026); a new product gets
              its price once it exists. */}
          {product?.id && (
            <InvoiceCard kind="product" id={product.id} ownName={product.name} number={product.code}
              cardStyle={{ border: '1px solid #2a3040', borderRadius: '10px', padding: '12px 14px' }}
              titleStyle={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#14b8a6', marginBottom: '10px' }} />
          )}
          {product?.id && (
            <ComplianceCard kind="product" id={product.id}
              cardStyle={{ border: '1px solid #2a3040', borderRadius: '10px', padding: '12px 14px' }}
              titleStyle={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#14b8a6', marginBottom: '10px' }} />
          )}

          {error && <div style={{ fontSize: '12px', color: '#fca5a5', background: '#7f1d1d', borderRadius: '6px', padding: '8px 12px' }}>{error}</div>}
        </div>
        <div style={s.modalFooter}>
          <button style={s.cancelBtn} onClick={onClose}>Cancel</button>
          <button style={{ ...s.addBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
            {saving ? 'Saving...' : product ? 'Save Changes' : 'Create Product'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, textarea, type, hint }) {
  return (
    <div style={s.field}>
      <label style={s.label}>{label}</label>
      {textarea
        ? <textarea style={{ ...s.input, minHeight: '70px', resize: 'vertical' }} value={value} onChange={e => onChange(e.target.value)} />
        : <input style={s.input} type={type || 'text'} value={value} onChange={e => onChange(e.target.value)} />
      }
      {hint && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px' }}>{hint}</div>}
    </div>
  );
}

// ── PART DETAIL ROUTE WRAPPER (/admin/parts/:id) ──────────────
export function PartDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  return (
    <SharedPartDetail
      partId={id}
      scope="full"
      onBack={() => navigate('/admin/parts')}
    />
  );
}

const COLS = '36px 2fr 1fr 1fr 1fr 28px 1.5fr 80px';
const s = {
  page:        cs.page,
  breadcrumb:  cs.breadcrumb,
  backBtn:     cs.backBtn,
  sep:         cs.sep,
  crumb:       cs.crumb,
  header:      cs.pageHeader,
  title:       cs.pageTitle,
  sub:         cs.pageSub,
  addBtn:      cs.btnPrimary,
  center:      cs.center,
  table:       cs.tableWrap,
  tableHeader: { ...cs.tableHeader, gridTemplateColumns: COLS, minWidth: '520px' },
  tableRow:    { ...cs.tableRow, gridTemplateColumns: COLS, minWidth: '520px' },
  partName:    { fontSize: '13px', fontWeight: '700' },
  partNum:     { fontSize: '10px', color: C.teal, fontFamily: 'monospace', marginTop: '2px' },
  cell:        { fontSize: '12px', color: '#9ca3af' },
  iconEdit:    { background: 'rgba(29,78,216,0.15)', border: '1px solid rgba(29,78,216,0.3)', color: '#93c5fd', borderRadius: '5px', width: '26px', height: '26px', padding: 0, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  iconDel:     { background: 'rgba(127,29,29,0.15)', border: '1px solid rgba(127,29,29,0.4)', color: '#fca5a5', borderRadius: '5px', width: '26px', height: '26px', padding: 0, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  iconRestore: { background: 'rgba(22,101,52,0.15)', border: '1px solid rgba(74,222,128,0.3)', color: '#86efac', borderRadius: '5px', width: '26px', height: '26px', padding: 0, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  overlay:     cs.overlay,
  modal:       cs.modal,
  modalHeader: cs.modalHeader,
  modalTitle:  cs.modalTitle,
  closeBtn:    cs.closeBtn,
  modalBody:   cs.modalBody,
  modalFooter: cs.modalFooter,
  sectionTitle:{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: C.teal, marginBottom: '10px', paddingBottom: '6px', borderBottom: `1px solid ${C.border}` },
  field:       { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' },
  label:       cs.label,
  input:       cs.input,
  cancelBtn:   cs.btnSecondary,
  searchWrap:  { position: 'relative', display: 'flex', alignItems: 'center' },
  searchIcon:  { position: 'absolute', left: '12px', color: C.mutedDark, fontSize: '13px', pointerEvents: 'none', zIndex: 1 },
  searchInput: { width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', color: C.text, padding: '10px 36px', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'system-ui, sans-serif' },
  clearBtn:    { position: 'absolute', right: '10px', background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '13px', padding: '2px 4px' },
  suggestions: { position: 'absolute', top: '100%', left: 0, right: 0, background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', zIndex: 20, marginTop: '4px', maxHeight: '300px', overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' },
  suggItem:    { display: 'flex', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${C.border}` },
};
